<template>
  <b-modal :title="helper.trans('labels.counterparty_statistics')"
           dialog-class="modal-xl"
           :modelValue="isVisible"
           footer-class="d-flex justify-content-end"
           @update:modelValue="isVisible = $event">
    <template #default>
      <LoaderWrapper ref="loader" class="h-100">
        <div class="p-3 pt-0" v-if="requisitesId">
          <div class="overflow-auto">
            <table class="table table-striped border-light align-middle text-left position-relative">
              <thead>
              <tr>
                <th class="text-capitalize-first">{{ helper.trans('labels.user_id') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('labels.use_count') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('labels.completed_count') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('labels.conversion_rate') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('labels.average_check') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('labels.days_since_last_use') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('labels.first_used_at') }}</th>
                <th class="text-capitalize-first">{{ helper.trans('labels.last_used_at') }}</th>
                <th></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in counterparties" :key="index" class="">
                <td>
                  <span v-b-tooltip.hover :title="item.userId">{{ item.userId.substring(0, 8) }}...</span>
                </td>
                <td>{{ numberFormatter.formatNumber(item.useCount) }}</td>
                <td>{{ numberFormatter.formatNumber(item.completedCount) }}</td>
                <td>{{ numberFormatter.formatNumber(item.conversionRate, 2) }}%</td>
                <td>{{ numberFormatter.formatNumber(Number.parseFloat(item.averageCheck), 2) }}</td>
                <td>{{ numberFormatter.formatNumber(item.daysSinceLastUse) }}</td>
                <td>
                  <span v-if="item.firstUsedAt">{{ dateHelper.format(item.firstUsedAt, 'YYYY-MM-DD') }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>{{ dateHelper.format(item.lastUsedAt, 'YYYY-MM-DD') }}</td>
                <td class="text-end">
                  <div class="d-flex gap-2 justify-content-end align-items-center">
                    <button type="button" @click="handleReattachClick(item.userId)" class="btn btn-sm btn-outline-primary text-nowrap text-capitalize-first">
                      {{ helper.trans('button.reattach') }}
                    </button>
                    <button type="button" @click="confirmDeleteCounterparty(item.userId)" class="btn btn-sm btn-outline-danger text-nowrap text-capitalize-first">
                      {{ helper.trans('button.unlink') }}
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="counterparties.length === 0">
                <td colspan="9" class="text-center text-muted py-3">
                  {{ helper.trans('labels.no_history') }}
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </LoaderWrapper>
    </template>
    <template #footer>
      <button type="button" @click.prevent="hide" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ helper.trans('button.ok') }}
      </button>
    </template>
  </b-modal>
  <ConfirmModal ref="confirmModal"></ConfirmModal>
  <ReattachRequisiteModal 
    ref="reattachModal" 
    @selected="handleReattach"
  ></ReattachRequisiteModal>
</template>

<script lang="ts">
import {defineComponent, ref} from 'vue'
import {helper} from "@/service/CommonHelper";
import APIClient from "@/service/APIClient";
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import {BModal} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import CounterpartyStatisticsDTO from "@/models/CounterpartyStatisticsDTO";
import ConfirmModal, {ConfirmModalProps} from "@/components/ConfirmModal.vue";
import {useNotification} from "@kyvg/vue3-notification";
import ReattachRequisiteModal from "@/components/ReattachRequisiteModal.vue";

const api = new APIClient();
const notification = useNotification();

export default defineComponent({
  components: {
    LoaderWrapper,
    BModal,
    ConfirmModal,
    ReattachRequisiteModal,
  },
  setup() {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const confirmModal = ref<InstanceType<typeof ConfirmModal> | null>(null)
    const reattachModal = ref<InstanceType<typeof ReattachRequisiteModal> | null>(null)
    const requisitesId = ref<number|null>(null)
    const counterparties = ref<CounterpartyStatisticsDTO[]>([])
    const isVisible = ref<boolean>(false)

    const show = async (id: number) => {
      requisitesId.value = id
      isVisible.value = true
      await load()
    }

    const hide = () => {
      isVisible.value = false
      requisitesId.value = null
    }

    const load = async () => {
      if (!requisitesId.value) {
        return;
      }

      loader.value?.show()
      try {
        counterparties.value = await api.requisitesCounterparties(requisitesId.value)
      } catch (error) {
        counterparties.value = []
      } finally {
        loader.value?.hide()
      }
    }

    const confirmDeleteCounterparty = (userId: string) => {
      if (!requisitesId.value) {
        return;
      }

      confirmModal.value?.show({
        title: helper.trans('labels.unlink_counterparty_confirm'),
        okTitle: helper.trans('button.unlink'),
        cancelTitle: helper.trans('button.cancel'),
        onOkClick: async () => {
          await deleteCounterparty(userId)
        }
      } as ConfirmModalProps)
    }

    const deleteCounterparty = async (userId: string) => {
      if (!requisitesId.value) {
        return;
      }

      loader.value?.show()
      try {
        await api.deleteRequisitesCounterparty(requisitesId.value, userId)
        notification.notify({title: helper.trans('labels.counterparty_unlinked'), type: 'success'})
        await load()
      } catch (error: any) {
        notification.notify({title: error.response?.data?.message || helper.trans('labels.error'), type: 'error'})
      } finally {
        loader.value?.hide()
      }
    }

    const handleReattachClick = async (userId: string) => {
      if (!requisitesId.value) {
        return;
      }
      
      try {
        const requisites = await api.getAvailableRequisitesForReattach(requisitesId.value, userId)
        if (requisites.length === 0) {
          notification.notify({title: helper.trans('labels.no_available_requisites'), type: 'warn'})
        } else {
          reattachModal.value?.show(userId, requisites)
        }
      } catch (error: any) {
        notification.notify({title: error.response?.data?.message || helper.trans('labels.error'), type: 'error'})
      }
    }

    const handleReattach = async (userId: string, newRequisitesId: number) => {
      if (!requisitesId.value) {
        return;
      }

      loader.value?.show()
      try {
        await api.reattachCounterparty(requisitesId.value, userId, newRequisitesId)
        notification.notify({title: helper.trans('labels.reattach_success'), type: 'success'})
        await load()
      } catch (error: any) {
        notification.notify({title: error.response?.data?.message || helper.trans('labels.reattach_error'), type: 'error'})
      } finally {
        loader.value?.hide()
      }
    }

    return {
      requisitesId,
      counterparties,
      isVisible,
      show,
      hide,
      load,
      loader,
      dateHelper,
      numberFormatter,
      helper,
      confirmDeleteCounterparty,
      confirmModal,
      reattachModal,
      handleReattachClick,
      handleReattach
    }
  }
})
</script>

